import { Fragment, useEffect, useState, type FormEvent } from 'react';
import { useSearchParams } from '../lib/routing';
import { HeaderContent, HEADER_TITLE_CLASS } from '../components/shell/app-header';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  ingestDocument,
  listDocuments,
  listPages,
  searchPages,
  type DocPage,
  type DocRow,
  type SearchHit,
} from '../lib/docs';
import { Alert } from '@/components/ui/alert';

const DOCS_PER_PAGE = 10;

const cardClass = "bg-card border border-border rounded-[16px] p-[14px]";
const boldClass = "font-extrabold";

export default function AdminDocs() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') ?? '';
  const docId = searchParams.get('doc');
  const page = Math.max(1, Number.parseInt(searchParams.get('page') ?? '1', 10) || 1);
  const dp = Math.max(1, Number.parseInt(searchParams.get('dp') ?? '1', 10) || 1);

  const [docs, setDocs] = useState<DocRow[] | null>(null);
  const [pages, setPages] = useState<DocPage[] | null>(null);
  const [hits, setHits] = useState<SearchHit[] | null>(null);
  const [qInput, setQInput] = useState(q);
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const open = docs?.find((d) => d.id === docId) ?? null;
  const pageIdx = pages && pages.length ? Math.min(page - 1, pages.length - 1) : 0;

  const patch = (obj: Record<string, string | number | null>, replace = false) => {
    const next = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(obj)) {
      if (v === null || v === '') next.delete(k);
      else next.set(k, String(v));
    }
    setSearchParams(next, { replace });
  };

  useEffect(() => {
    listDocuments().then(setDocs).catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  useEffect(() => {
    setQInput(q);
  }, [q]);

  useEffect(() => {
    if (qInput === q) return;
    const t = setTimeout(() => patch({ q: qInput, page: null }, true), 350);
    return () => clearTimeout(t);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [qInput]);

  useEffect(() => {
    if (!q.trim()) {
      setHits(null);
      return;
    }
    let cancelled = false;
    searchPages(q.trim())
      .then((r) => !cancelled && setHits(r))
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)));
    return () => { cancelled = true; };
  }, [q]);

  useEffect(() => {
    if (!docId) {
      setPages(null);
      return;
    }
    let cancelled = false;
    setPages(null);
    listPages(docId)
      .then((p) => !cancelled && setPages(p))
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)));
    return () => { cancelled = true; };
  }, [docId]);

  const onIngest = async (e: FormEvent) => {
    e.preventDefault();
    const u = url.trim();
    if (!u || busy) return;
    setBusy(true);
    setError(null);
    setMsg('Import + OCR en cours (ça peut prendre ~30 s)…');
    try {
      const r = await ingestDocument(u);
      setMsg(`Importé : « ${r.title} » (${r.page_count} pages).`);
      setUrl('');
      setDocs(await listDocuments());
    } catch (e2) {
      setMsg(null);
      setError(e2 instanceof Error ? e2.message : String(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <HeaderContent width="xl" showBurger>
        <h1 className={HEADER_TITLE_CLASS}>Documents</h1>
      </HeaderContent>
      <main className="max-w-[1240px] mx-auto px-4 pb-16 pt-4 flex flex-col gap-4">

        <form onSubmit={onIngest} className={cardClass}>
          <div className="font-extrabold mb-2">Importer un PDF par URL</div>
          <div className="flex gap-2 flex-wrap">
            <input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://…/document.pdf"
              disabled={busy}
              className="flex-1 min-w-0 min-w-[180px] py-[10px] px-3 border border-border rounded-[10px] font-[inherit] text-sm bg-secondary"
            />
            <button type="submit" disabled={busy || !url.trim()}
              className={cn(
                "py-[10px] px-[18px] bg-naia-blue text-card border-none rounded-sm font-extrabold cursor-pointer font-[inherit] shadow-card",
                busy && "bg-naia-info-bg text-muted-foreground cursor-wait shadow-none"
              )}>
              {busy ? 'OCR…' : 'Importer'}
            </button>
          </div>
          {msg && <div className="mt-2 text-[13px] text-muted-foreground font-bold">{msg}</div>}
        </form>

        {error && <Alert>{error}</Alert>}

        <form onSubmit={(e) => { e.preventDefault(); patch({ q: qInput, page: null }); }} className={cardClass}>
          <div className="font-extrabold mb-2">Recherche plein-texte (tous les docs)</div>
          <div className="flex gap-2">
            <input value={qInput} onChange={(e) => setQInput(e.target.value)} placeholder="ex: maîtrise satisfaisante"
              className="flex-1 min-w-0 py-[10px] px-3 border border-border rounded-[10px] font-[inherit] text-sm bg-secondary" />
            {qInput && (
              <button type="button" onClick={() => { setQInput(''); patch({ q: null, page: null }, true); }}
                className="py-[10px] px-3 bg-card border border-border rounded-[10px] cursor-pointer font-[inherit]">✕</button>
            )}
          </div>
        </form>

        <div className={cn("flex flex-col gap-4", open && "md:flex-row md:items-start")} data-open={open ? 'true' : 'false'}>
          <div className={cn("flex flex-col gap-2 min-w-0", open ? "md:flex-[0_0_30%]" : "flex-1")}>
            {q.trim() ? (
              <>
                <div className={boldClass}>
                  Résultats {hits ? `(${groupHits(hits).length} doc${groupHits(hits).length > 1 ? 's' : ''})` : '…'}
                </div>
                {hits && hits.length === 0 && <div className="text-[13px] text-muted-foreground">Aucun résultat.</div>}
                {hits && groupHits(hits).map((g) => (
                  <div key={g.document_id} className={cn("border border-border rounded-[12px] overflow-hidden bg-card", docId === g.document_id && "border-naia-blue")}>
                    <div className="flex items-baseline gap-2 px-3 py-2 bg-secondary border-b border-naia-line-btn">
                      <span className="font-extrabold text-[13px]">{g.title ?? '(sans titre)'}</span>
                      <span className="text-[12px] text-muted-foreground font-bold">{g.hits.length} {g.hits.length > 1 ? 'passages' : 'passage'}</span>
                    </div>
                    <div className="flex flex-col">
                      {g.hits.map((h, i) => (
                        <button key={i} type="button" onClick={() => patch({ doc: h.document_id, page: h.page_number })}
                          className={cn("text-left bg-transparent border-none border-t border-border px-3 py-[9px] cursor-pointer font-[inherit] flex gap-[10px]", i === 0 && "border-t-0")}>
                          <span className="shrink-0 font-extrabold text-[12px] text-naia-blue min-w-[42px]">p.{h.page_number}</span>
                          <span className="text-[13px] text-foreground">{renderSnippet(h.snippet)}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </>
            ) : (
              <>
                {(() => {
                  const total = docs?.length ?? 0;
                  const pageCount = Math.max(1, Math.ceil(total / DOCS_PER_PAGE));
                  const cur = Math.min(dp, pageCount);
                  return (
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className={boldClass}>Documents ({docs ? total : '…'})</div>
                      {total > DOCS_PER_PAGE && (
                        <div className="flex items-center gap-[6px] ml-auto">
                          <Button variant="outline" disabled={cur <= 1} onClick={() => patch({ dp: cur - 1 }, true)} className="w-9 h-9 min-h-0 p-0 rounded-[8px] font-extrabold">←</Button>
                          <span className="text-[13px] font-bold">{cur} / {pageCount}</span>
                          <Button variant="outline" disabled={cur >= pageCount} onClick={() => patch({ dp: cur + 1 }, true)} className="w-9 h-9 min-h-0 p-0 rounded-[8px] font-extrabold">→</Button>
                        </div>
                      )}
                    </div>
                  );
                })()}
                {docs?.slice((Math.min(dp, Math.max(1, Math.ceil(docs.length / DOCS_PER_PAGE))) - 1) * DOCS_PER_PAGE, Math.min(dp, Math.max(1, Math.ceil(docs.length / DOCS_PER_PAGE))) * DOCS_PER_PAGE).map((d) => (
                  <button key={d.id} type="button" onClick={() => patch({ doc: d.id, page: 1 })}
                    className={cn("bg-card border border-border rounded-[16px] p-[14px] text-left cursor-pointer font-[inherit]", docId === d.id && "border-naia-blue")}>
                    <div className={boldClass}>{d.title ?? '(sans titre)'}</div>
                    <div className="text-[12px] text-muted-foreground mt-0.5 break-all">{d.source_url}</div>
                    <div className="text-[12px] mt-1">
                      <Badge status={d.ocr_status} /> · {d.page_count ?? '?'} pages · {new Date(d.created_at).toLocaleString('fr-FR')}
                    </div>
                    {d.ocr_error && <div className="text-[12px] text-naia-error mt-1">{d.ocr_error}</div>}
                  </button>
                ))}
              </>
            )}
          </div>

          {open && (
            <div className="flex flex-col gap-2 min-w-0 flex-1 md:sticky md:top-2">
              <div className={cardClass}>
                <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                  <div className={boldClass}>{open.title ?? '(sans titre)'}</div>
                  <button type="button" onClick={() => patch({ doc: null, page: null })}
                    className="border border-border bg-transparent rounded-[8px] px-[10px] py-1 cursor-pointer font-extrabold">✕</button>
                </div>
                {!pages ? (
                  <div className="p-4 text-muted-foreground">Chargement des pages…</div>
                ) : pages.length === 0 ? (
                  <div className="p-4 text-muted-foreground">Aucune page.</div>
                ) : (
                  <>
                    <div className="flex items-center gap-2 my-[10px]">
                      <Button variant="outline" disabled={pageIdx === 0} onClick={() => patch({ page: pages[pageIdx - 1]?.page_number ?? 1 }, true)} className="w-9 h-9 min-h-0 p-0 rounded-[8px] font-extrabold">←</Button>
                      <span className="text-[13px] font-bold">Page {pages[pageIdx]?.page_number ?? pageIdx + 1} / {pages.length}</span>
                      <Button variant="outline" disabled={pageIdx >= pages.length - 1} onClick={() => patch({ page: pages[pageIdx + 1]?.page_number ?? pages.length }, true)} className="w-9 h-9 min-h-0 p-0 rounded-[8px] font-extrabold">→</Button>
                      <select value={pageIdx} onChange={(e) => patch({ page: pages[Number(e.target.value)]?.page_number ?? 1 }, true)}
                        className="ml-auto py-[6px] px-2 border border-border rounded-[8px] font-[inherit]">
                        {pages.map((p, i) => <option key={i} value={i}>p.{p.page_number}</option>)}
                      </select>
                    </div>
                    <pre className="whitespace-pre-wrap break-words font-mono text-[13px] leading-[1.45] bg-secondary border border-border rounded-[10px] p-3 max-h-[55vh] overflow-auto m-0">
                      {pages[pageIdx]?.ocr_text || '(page vide)'}
                    </pre>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
    </>
  );
}

function groupHits(hits: SearchHit[]): Array<{ document_id: string; title: string | null; hits: SearchHit[] }> {
  const order: string[] = [];
  const by = new Map<string, { document_id: string; title: string | null; hits: SearchHit[] }>();
  for (const h of hits) {
    let g = by.get(h.document_id);
    if (!g) {
      g = { document_id: h.document_id, title: h.title, hits: [] };
      by.set(h.document_id, g);
      order.push(h.document_id);
    }
    g.hits.push(h);
  }
  for (const g of by.values()) g.hits.sort((a, b) => a.page_number - b.page_number);
  return order.map((id) => by.get(id)!);
}

function Badge({ status }: { status: DocRow['ocr_status'] }) {
  return (
    <span className={cn(
      "border border-border rounded-[6px] py-[1px] px-[6px] font-extrabold text-[11px] bg-secondary",
      status === 'done' && "bg-naia-green-bg",
      status === 'failed' && "bg-naia-info-bg"
    )}>
      {status}
    </span>
  );
}

function renderSnippet(snippet: string) {
  const parts = snippet.split(/(<<|>>)/);
  let on = false;
  return (
    <>
      {parts.map((p, i) => {
        if (p === '<<') { on = true; return null; }
        if (p === '>>') { on = false; return null; }
        return on ? <mark key={i} className="bg-naia-green-bg">{p}</mark> : <Fragment key={i}>{p}</Fragment>;
      })}
    </>
  );
}
